import React, { useState } from "react";
import { getUrl, uploadData } from "aws-amplify/storage";
import { generateClient } from "aws-amplify/api";
import { v4 as uuid } from "uuid";
import { createBook, createImage } from "../../graphql/mutations";
import { CreateBookMutation } from "../../API";
import { useBanner } from "../CustomHooks/useBanner";
import "./FileUpload.css";
import { Link } from "react-router-dom";

const client = generateClient();

const FileUpload = () => {
  const [fileInputs, setFileInputs] = useState<(File | null)[]>([null]);
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const { bannerMessage, bannerVisible, isError, showBanner } = useBanner();

  const handleUpload = async () => {
    if (fileInputs.length === 0 || !title || !author) {
      showBanner(
        "Please fill in all fields and select at least one file",
        true
      );
      return;
    }

    setIsUploading(true);

    try {
      const bookDetails = {
        title,
        authorName: author,
      };

      const createBookResponse = (await client.graphql({
        query: createBook,
        variables: { input: bookDetails },
      })) as { data: CreateBookMutation };

      const bookId = createBookResponse.data.createBook?.id;
      if (!bookId) throw new Error("Book ID not returned from createBook");

      const uploadPromises = fileInputs.map(async (file, index) => {
        if (!file) return;

        const fileKey = `photos/${uuid()}_${index}_${file.name}`;

        await uploadData({
          key: fileKey,
          data: file,
          options: {
            contentType: file.type,
          },
        }).result;

        await client.graphql({
          query: createImage,
          variables: {
            input: {
              key: fileKey,
              order: index,
              bookID: bookId,
            },
          },
        });

        await getUrl({ key: fileKey }); // Just to confirm it was uploaded
      });

      await Promise.all(uploadPromises);
      showBanner("Files uploaded successfully!");

      setTitle("");
      setAuthor("");
      setFileInputs([null]);
    } catch (error: any) {
      if (error.errors) {
        console.error("GraphQL errors:", error.errors);
      } else {
        console.error("Error uploading files:", error);
      }
      showBanner("Error occurred during file upload.", true);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files && event.target.files[0]) {
      const newFiles = [...fileInputs];
      newFiles[index] = event.target.files[0];
      setFileInputs(newFiles);
    }
  };

  const handleAddPage = () => {
    setFileInputs([...fileInputs, null]);
  };

  return (
    <div>
      <div className="file-upload-container">
        <div className="file-upload-contents">
          <Link to="/library" className="back-button">
            ← Back to Library
          </Link>
          <h1>Upload a book</h1>
          {bannerVisible && (
            <div className={`banner ${isError ? "banner-error" : ""}`}>
              {bannerMessage}
            </div>
          )}
          <div className="file-upload-input">
            <div className="input-row">
              <label htmlFor="title">Title:</label>
              <input
                id="title"
                type="text"
                placeholder="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="input-row">
              <label htmlFor="author">Author:</label>
              <input
                id="author"
                type="text"
                placeholder="Author"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
              />
            </div>
            {fileInputs.map((file, index) => (
              <div key={index} className="file-upload-page input-row">
                <label>Page {index + 1}:</label>
                <input
                  type="file"
                  onChange={(e) => handleFileChange(index, e)}
                />
              </div>
            ))}
            <button type="button" onClick={handleAddPage}>
              + Add Page
            </button>
          </div>
          <button
            className="upload-button"
            onClick={handleUpload}
            disabled={isUploading}
          >
            {isUploading ? "Uploading..." : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FileUpload;
